import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { Button, Form, Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getBudgetLines } from '../../clients/budget_line_client';
import { getUserSelectList } from '../../clients/user_client';
import BudgetsTable from '../../components/Budget/BudgetsTable';
import BudgetsWorkspaceShell from '../../components/Budget/BudgetsWorkspaceShell';
import '../../components/Glass/GlassStyle.css';
import BudgetLineFormModal from '../../components/LabData/BudgetLineFormModal';
import { invalidateBudgetLineSelectCache } from '../../components/LabData/BudgetLineSelect';
import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import BudgetCache from '../../config_and_helpers/budget_cache';
import {
  isPartialListSearchQuery,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';
import {
  mergeBudgetListRow,
  resolveBudgetPiId,
  upsertBudgetListRow,
} from '../../utils/budgetListRow';

const LOG_CONTEXT = 'BudgetsPage';

/** @typedef {'all'|'active'|'inactive'} ActiveFilter */

const normalizeParam = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/**
 * Client-side filter by search text (number/code/name).
 *
 * @param {Array} rows
 * @param {string} search
 * @returns {Array}
 */
function filterBySearch(rows, search) {
  const q = String(search || '')
    .trim()
    .toLowerCase();
  if (isPartialListSearchQuery(q)) return [];
  if (q.length < 2) return rows;
  return rows.filter(row => {
    const number = String(row.number ?? '').toLowerCase();
    const code = String(row.code ?? '').toLowerCase();
    const name = String(row.name ?? '').toLowerCase();
    return number.includes(q) || code.includes(q) || name.includes(q);
  });
}

/**
 * @param {Array} rows
 * @param {string} piId
 * @returns {Array}
 */
function filterByPi(rows, piId) {
  if (piId == null || String(piId).trim() === '') return rows;
  const target = String(piId);
  return rows.filter(row => resolveBudgetPiId(row) === target);
}

/**
 * @param {Array} rows
 * @param {ActiveFilter} activeFilter
 * @returns {Array}
 */
function filterByActive(rows, activeFilter) {
  if (activeFilter === 'active') {
    return rows.filter(row => row.is_active !== false);
  }
  if (activeFilter === 'inactive') {
    return rows.filter(row => row.is_active === false);
  }
  return rows;
}

/**
 * @param {Array} rows
 * @param {string} sortBy
 * @param {string} sortDirection
 * @returns {Array}
 */
function sortRows(rows, sortBy, sortDirection) {
  if (!sortBy) return rows;
  const dir = sortDirection === 'desc' ? -1 : 1;
  const copy = [...rows];
  copy.sort((a, b) => {
    let av;
    let bv;
    if (sortBy === 'number') {
      av = String(a.number ?? a.code ?? '');
      bv = String(b.number ?? b.code ?? '');
      return av.localeCompare(bv, undefined, { numeric: true }) * dir;
    }
    if (sortBy === 'allocated_amount') {
      av = parseFloat(a.allocated_amount);
      bv = parseFloat(b.allocated_amount);
      if (!Number.isFinite(av)) av = 0;
      if (!Number.isFinite(bv)) bv = 0;
      return (av - bv) * dir;
    }
    av = String(a[sortBy] ?? '');
    bv = String(b[sortBy] ?? '');
    return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * dir;
  });
  return copy;
}

/**
 * Procure admin: institutional Budget CRUD with infinite scroll pagination.
 */
const BudgetsPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const debounceTimeoutRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(true);

  const [allRows, setAllRows] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [piFilter, setPiFilter] = useState('');
  const [piOptions, setPiOptions] = useState([]);
  /** @type {[ActiveFilter, Function]} */
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('number');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editLine, setEditLine] = useState(null);
  const pendingSavedRowRef = useRef(null);

  const fetchBudgets = useCallback(
    ({ nextPage = null, active = 'all' } = {}, newSearch = false) => {
      if (!token) return Promise.resolve();

      setLoading(true);
      if (newSearch) {
        setAllRows([]);
        setHasMore(true);
        setNextPageUrl(null);
      }
      isLoadingRef.current = true;

      const apiIsActive = active === 'active' ? true : undefined;
      logInfo(LOG_CONTEXT, 'fetch budgets page', {
        newSearch,
        paginating: Boolean(nextPage),
        active,
      });

      return getBudgetLines(token, {
        isActive: apiIsActive,
        nextPage,
      })
        .then(res => {
          if (!isMountedRef.current) return;
          if (!res.success) {
            showToast(
              res.messages?.[0] || 'Failed to load budgets',
              'error',
              'top-right',
              5000
            );
            logError(LOG_CONTEXT, 'load failed', { messages: res.messages });
            if (newSearch) setAllRows([]);
            setHasMore(false);
            setNextPageUrl(null);
            return;
          }
          const pageRows = Array.isArray(res.data) ? res.data : [];
          if (nextPage) {
            setAllRows(prev => [...prev, ...pageRows]);
          } else {
            setAllRows(pageRows);
          }
          const next = res.nextPage || null;
          setNextPageUrl(next);
          setHasMore(Boolean(next) && !res.reachedEnd);
        })
        .finally(() => {
          if (!isMountedRef.current) return;
          isLoadingRef.current = false;
          setLoading(false);
        });
    },
    [token]
  );

  useEffect(() => {
    isMountedRef.current = true;
    fetchBudgets({ active: activeFilter }, true);
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchBudgets, activeFilter]);

  useEffect(() => {
    if (!token) return;
    getUserSelectList(token, setPiOptions).then(result => {
      if (!result?.success) {
        logError(LOG_CONTEXT, 'getUserSelectList failed', result?.error);
      }
    });
  }, [token]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchInput = val => {
    setSearchInput(val);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (isPartialListSearchQuery(val)) {
      setIsSearching(false);
      setDebouncedSearch(val);
      return;
    }
    if (val.length >= 2) {
      setIsSearching(true);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setIsSearching(false);
      logInfo(LOG_CONTEXT, 'search applied', { qLen: val.length });
    }, 300);
  };

  const handlePiChange = val => {
    setPiFilter(val || '');
    logInfo(LOG_CONTEXT, 'pi filter', { piId: val || null });
  };

  const handleSort = (key, direction) => {
    setSortBy(key);
    setSortDirection(direction);
    logInfo(LOG_CONTEXT, 'sort', { key, direction });
  };

  const displayedRows = useMemo(() => {
    let rows = filterByActive(allRows, activeFilter);
    rows = filterByPi(rows, piFilter);
    rows = filterBySearch(rows, debouncedSearch);
    return sortRows(rows, sortBy, sortDirection);
  }, [
    allRows,
    activeFilter,
    piFilter,
    debouncedSearch,
    sortBy,
    sortDirection,
  ]);

  const handleSaved = saved => {
    const wasEdit = Boolean(editLine?.id);
    invalidateBudgetLineSelectCache();
    setShowModal(false);
    setEditLine(null);

    const row = saved && typeof saved === 'object' ? saved : null;
    logInfo(LOG_CONTEXT, 'budget saved', {
      id: row?.id ?? null,
      wasEdit,
      piId: resolveBudgetPiId(row),
    });

    if (row?.id) {
      pendingSavedRowRef.current = row;
      BudgetCache.set(
        row.id,
        mergeBudgetListRow(BudgetCache.get(row.id) || {}, row)
      );
      setAllRows(prev => upsertBudgetListRow(prev, row));
    }

    if (wasEdit) {
      return;
    }

    fetchBudgets({ active: activeFilter }, true).then(() => {
      const pending = pendingSavedRowRef.current;
      if (!pending?.id || !isMountedRef.current) return;
      setAllRows(prev => upsertBudgetListRow(prev, pending));
      logInfo(LOG_CONTEXT, 'create overlay after list refetch', {
        id: pending.id,
      });
    });
  };

  /**
   * Drop a deleted budget from the list and refresh quote/project/order pickers.
   *
   * @param {string|number} id
   */
  const handleDeleted = id => {
    logInfo(LOG_CONTEXT, 'budget deleted', { id });
    setAllRows(prev => prev.filter(row => String(row.id) !== String(id)));
    invalidateBudgetLineSelectCache();
  };

  const showInitialSpinner =
    (loading && allRows.length === 0) || isSearching;
  const hasActiveSearch = String(debouncedSearch).trim().length >= 2;

  return (
    <BudgetsWorkspaceShell>
      <div className="budgets-page-heading">
        <h2>Budgets</h2>
      </div>
      <p className="budgets-page-sub">
        Institutional budgets for quote, project, and order funding. Spend and
        burn metrics live under Lab Data.
      </p>

      <div className="budgets-toolbar">
        <GenericSearchFilter
          entityType="budgets"
          placeHolderLabel="Primary investigator"
          searchInput={searchInput}
          onSearchInput={handleSearchInput}
          onEntityChange={handlePiChange}
          entity={piFilter}
          entityOptionsList={piOptions}
          customActionButton={
            isViewer ? null : (
            <Button
              className="budgets-primary-btn"
              onClick={() => {
                setEditLine(null);
                setShowModal(true);
              }}
            >
              Create Budget
            </Button>
            )
          }
          trailingActionButton={
            <Form.Select
              className="budgets-active-filter"
              aria-label="Active status filter"
              value={activeFilter}
              onChange={e => {
                setActiveFilter(/** @type {ActiveFilter} */ (e.target.value));
                logInfo(LOG_CONTEXT, 'active filter', {
                  value: e.target.value,
                });
              }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          }
        />
      </div>

      {showInitialSpinner ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 budgets-page-sub">Searching budgets...</div>
          )}
        </div>
      ) : displayedRows.length === 0 && !loading ? (
        <div className="budgets-empty">
          <h5 className="mb-2">No budgets found</h5>
          <p className="mb-3">
            {hasActiveSearch
              ? `No budgets match your search for "${searchInput}"`
              : piFilter || activeFilter !== 'all'
                ? 'No budgets match the current filters'
                : 'No budgets available'}
          </p>
          {hasActiveSearch && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setSearchInput('');
                setDebouncedSearch('');
              }}
            >
              Clear search
            </Button>
          )}
        </div>
      ) : (
        <InfiniteScroll
          pageStart={0}
          loadMore={() => {
            if (isLoadingRef.current) return;
            if (!nextPageUrl) return;

            try {
              const nextUrl = new URL(nextPageUrl, window.location.origin);
              const urlActive = normalizeParam(
                nextUrl.searchParams.get('is_active')
              );
              if (
                activeFilter === 'active' &&
                urlActive !== 'true' &&
                urlActive !== '1'
              ) {
                // Stale next URL after filter change — force refresh
                fetchBudgets({ active: activeFilter }, true);
                return;
              }
            } catch (error) {
              logError(LOG_CONTEXT, 'next-page-url-parse-error', {
                nextPageUrl,
                error: String(error),
              });
            }

            fetchBudgets(
              { nextPage: nextPageUrl, active: activeFilter },
              false
            );
          }}
          hasMore={hasMore && !loading}
          loader={
            <div key="budgets-loader" className="text-center py-2">
              <Spinner animation="border" size="sm" role="status" />
              <div className="mt-1">
                <small className="text-muted">Loading more budgets...</small>
              </div>
            </div>
          }
          useWindow
          threshold={INFINITE_SCROLL_THRESHOLD_PX}
        >
          <div className="glass-table-wrap">
            <BudgetsTable
              budgetsList={displayedRows}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              onEdit={row => {
                setEditLine(row);
                setShowModal(true);
              }}
              onDeleted={handleDeleted}
            />
          </div>
        </InfiniteScroll>
      )}

      <BudgetLineFormModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditLine(null);
        }}
        token={token}
        line={editLine}
        onSaved={handleSaved}
      />
    </BudgetsWorkspaceShell>
  );
};

export default BudgetsPage;
